/**
 * Просмотрщик архива камер салона Macroscop — общий для раздела «Видео» и окна события сделки во
 * вкладке «Договор». Все камеры салона чипами по группам (камеры события предвыбраны и помечены
 * точкой), из выбранных одна активная: вид «Одна» — большой кадр активной и синхронные кадры
 * остальных, «Сетка» — все выбранные плитками на один момент. Шкала с зумом (`Timeline`), кадры
 * вокруг с шагом 1 с … 5 мин, разметка «Начало / Конец» — одна на все камеры, «Сделать ролик» —
 * по ролику на каждую выбранную камеру (по 2 разом, как экспорт Macroscop); список роликов — эти и
 * прошлые из истории (`ClipList`). Лента кадров прокручивается, клик по кадру — меню «Показать /
 * Начало / Конец», отрезок подсвечен. Вкладка «Инструкция» — `web/src/help/video.md`. Видео — только
 * кадры JPEG по запросу: большой кадр грузим fetch'ем с отменой, пока идёт — прежний затемнён.
 * Спека `docs/specs/2026-10-23-video-design.md`.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, ChevronLeft, ChevronRight, Film, LayoutGrid, LoaderCircle, RotateCw, Square, Video, X } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Prose } from "@/components/Prose";
import { type ClipItem, ClipList } from "@/components/video/ClipList";
import { DAY, DAY_WIN, type Mark, Timeline, type Win, around, clampWin, spans } from "@/components/video/Timeline";
import help from "@/help/video.md?raw";
import {
  type DealVideoEvent,
  atOf,
  clock,
  dayOf,
  dur,
  frameUrl,
  loadFrame,
  makeClip,
  secOf,
  shortDay,
  videoCamerasQuery,
  videoClipsQuery,
  videoFragmentsQuery,
} from "@/lib/video";

const STEPS: [number, string][] = [
  [1, "1 с"],
  [5, "5 с"],
  [10, "10 с"],
  [30, "30 с"],
  [60, "1 мин"],
  [300, "5 мин"],
];
// Порядок групп камер: у стойки и на входе ищут чаще всего.
const GROUPS = ["Рабочий стол", "Стол", "Вход", "Зал", "Общее"];
const btn = "inline-flex h-8 items-center gap-1 rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 text-[12.5px] hover:bg-bg-subtle disabled:opacity-50";
const primary = "inline-flex h-8 items-center gap-1 rounded-[var(--radius-ui)] bg-accent px-3 text-[12.5px] font-medium text-white hover:opacity-90 disabled:opacity-50";
const chip = "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[12px]";

type Cam = { id: number; name: string; grp?: string | null; status?: string | null };
export type StudioView = "one" | "grid";
export type StudioState = { camera: number; cams: number[]; at: string; from: string | null; to: string | null; view: StudioView };

/** Ширина окна ≥ 1280: синхронные кадры — столбцом справа, кадров вокруг — 9. */
function useWide() {
  const [wide, setWide] = useState(() => window.innerWidth >= 1280);
  useEffect(() => {
    const on = () => setWide(window.innerWidth >= 1280);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return wide;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Большой кадр: запрос уходит, когда момент 150 мс не меняется (зажатая стрелка не шлёт пачку),
 * прежний запрос отменяется, поздний ответ старого кадра отбрасывается по номеру. Пока грузится —
 * на экране прежний кадр той же камеры.
 */
function useFrame(camera: number, at: string, w: number) {
  const [shown, setShown] = useState<{ url: string; at: string; camera: number } | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "slow" | "none" | "fail">("loading");
  const [retry, setRetry] = useState(0);
  const seq = useRef(0);
  const url = useRef<string | null>(null);
  useEffect(() => () => void (url.current && URL.revokeObjectURL(url.current)), []);
  useEffect(() => {
    const my = ++seq.current;
    const ctrl = new AbortController();
    setState("loading");
    const slow = setTimeout(() => my === seq.current && setState((s) => (s === "loading" ? "slow" : s)), 4000);
    const go = setTimeout(() => {
      loadFrame(frameUrl(camera, at, w), ctrl.signal, true)
        .then((blob) => {
          if (my !== seq.current) return;
          if (url.current) URL.revokeObjectURL(url.current);
          url.current = URL.createObjectURL(blob);
          setShown({ url: url.current, at, camera });
          setState("ok");
        })
        .catch((e: { name?: string; status?: number }) => {
          if (my !== seq.current || e.name === "AbortError") return;
          setState(e.status === 404 ? "none" : "fail");
        })
        .finally(() => clearTimeout(slow));
    }, 150);
    return () => (clearTimeout(go), clearTimeout(slow), ctrl.abort());
  }, [camera, at, w, retry]);
  return { shown: shown?.camera === camera ? shown : null, state, retry: () => setRetry((n) => n + 1) };
}

function BigFrame({ camera, at, name }: { camera: number; at: string; name: string }) {
  const { shown, state, retry } = useFrame(camera, at, 1280);
  const busy = state === "loading" || state === "slow";
  const stale = shown?.at !== at;
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded bg-black" data-annot="кадр">
      {shown && <img src={shown.url} alt={clock(secOf(shown.at))} className={`size-full object-contain transition-opacity ${stale ? "opacity-50" : ""}`} />}
      <span className="absolute left-2 top-2 inline-flex items-center gap-1.5 rounded bg-black/65 px-1.5 py-0.5 text-[12px] text-white tabular-nums">
        {shortDay(at)} {clock(secOf(at))}
        {busy && <LoaderCircle size={13} className="animate-spin" />}
      </span>
      <span className="absolute bottom-2 left-2 rounded bg-black/55 px-1.5 py-0.5 text-[11.5px] text-white">{name}</span>
      {state === "slow" && <span className="absolute right-2 top-2 rounded bg-black/65 px-1.5 py-0.5 text-[11.5px] text-white">Macroscop отвечает медленно…</span>}
      {state === "none" && <span className="absolute right-2 top-2 rounded bg-black/65 px-1.5 py-0.5 text-[11.5px] text-err">нет кадра на {clock(secOf(at))}</span>}
      {state === "fail" && (
        <button type="button" onClick={retry} className="absolute right-2 top-2 inline-flex items-center gap-1 rounded bg-black/65 px-1.5 py-0.5 text-[11.5px] text-err">
          Macroscop не ответил · <RotateCw size={12} /> повторить
        </button>
      )}
      {!shown && busy && (
        <div className="absolute inset-0 grid place-items-center text-white/70">
          <LoaderCircle size={28} className="animate-spin" />
        </div>
      )}
    </div>
  );
}

/** Плитка кадра: индикатор, пока кадр в очереди или грузится; `lazy` — грузится, только когда
 * показалась на экране (лента кадров длинная). Родитель ключует её камерой и временем. */
function Tile({
  camera,
  at,
  w,
  title,
  active,
  lazy,
  tint,
  badge,
  onClick,
  onDoubleClick,
}: {
  camera: number;
  at: string;
  w: number;
  title?: string;
  active?: boolean;
  lazy?: boolean;
  tint?: boolean;
  badge?: ReactNode;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onDoubleClick?: () => void;
}) {
  const [state, setState] = useState<"loading" | "ok" | "failed">("loading");
  const [src, setSrc] = useState<string | null>(null);
  const [seen, setSeen] = useState(!lazy);
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (seen || !ref.current) return;
    const io = new IntersectionObserver((e) => e.some((x) => x.isIntersecting) && setSeen(true), { rootMargin: "0px 200px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen]);
  useEffect(() => {
    if (!seen) return;
    const ctrl = new AbortController();
    let made: string | null = null;
    loadFrame(frameUrl(camera, at, w), ctrl.signal).then(
      (blob) => (setSrc((made = URL.createObjectURL(blob))), setState("ok")),
      (e: { name?: string }) => e.name === "AbortError" || setState("failed"),
    );
    return () => {
      ctrl.abort();
      if (made) URL.revokeObjectURL(made);
    };
  }, [camera, at, w, seen]);
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      className={`group w-full overflow-hidden rounded border text-left ${active ? "border-err ring-1 ring-err" : tint ? "border-accent" : "border-border hover:border-accent"}`}
    >
      <div className="relative aspect-video bg-bg-subtle">
        {src && <img src={src} alt="" decoding="async" className="size-full object-cover" />}
        {state === "loading" && (
          <div className="absolute inset-0 grid animate-pulse place-items-center text-fg-muted">
            <LoaderCircle size={16} className="animate-spin" />
          </div>
        )}
        {state === "failed" && <div className="absolute inset-0 grid place-items-center text-[11.5px] text-fg-muted">нет кадра</div>}
        {tint && <div className="pointer-events-none absolute inset-0 bg-accent/15" />}
        {badge && <span className="absolute left-1 top-1 rounded bg-accent px-1 text-[10.5px] font-medium text-white">{badge}</span>}
        {title && <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1.5 py-0.5 text-[11px] text-white">{title}</span>}
      </div>
      {!title && <div className={`px-1 text-[10.5px] tabular-nums ${tint ? "bg-accent/10 text-accent-text" : "text-fg-muted"}`}>{clock(secOf(at))}</div>}
    </button>
  );
}

/**
 * Лента кадров активной камеры: ±20 шагов вокруг курсора, прокручивается мышью (кадры грузятся по
 * мере прокрутки). Клик по кадру — меню: показать его, сделать началом или концом ролика; кадры
 * отрезка подсвечены, крайние — метками «начало» / «конец». Пока курсор на месте, лента не прыгает.
 */
const AROUND = 20;
function FrameStrip({
  camera,
  day,
  center,
  step,
  from,
  to,
  wide,
  onPick,
  onFrom,
  onTo,
}: {
  camera: number;
  day: string;
  center: number;
  step: number;
  from: number | null;
  to: number | null;
  wide: boolean;
  onPick: (s: number) => void;
  onFrom: (s: number) => void;
  onTo: (s: number) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<{ t: number; x: number; y: number } | null>(null);
  useEffect(() => {
    const el = box.current;
    const c = el?.querySelector<HTMLElement>('[data-center="1"]');
    if (el && c) el.scrollLeft = c.offsetLeft - el.clientWidth / 2 + c.clientWidth / 2;
  }, [center, step]);
  useEffect(() => {
    if (!menu) return;
    const close = (e: Event) => !(e.target as HTMLElement).closest?.("[data-strip-menu]") && setMenu(null);
    const key = (e: KeyboardEvent) => e.key === "Escape" && (e.stopPropagation(), setMenu(null));
    document.addEventListener("pointerdown", close);
    window.addEventListener("keydown", key, true);
    return () => (document.removeEventListener("pointerdown", close), window.removeEventListener("keydown", key, true));
  }, [menu]);
  const times: number[] = [];
  for (let k = -AROUND; k <= AROUND; k++) {
    const t = center + k * step;
    if (t >= 0 && t < DAY) times.push(t);
  }
  // Метка — на кадре, ближайшем к краю отрезка (край может быть не на сетке шага).
  const nearest = (edge: number | null) => (edge == null ? null : times.reduce((a, b) => (Math.abs(b - edge) < Math.abs(a - edge) ? b : a), times[0] ?? 0));
  const fromAt = nearest(from);
  const toAt = nearest(to);
  const pick = (fn: (s: number) => void) => () => (menu && fn(menu.t), setMenu(null));
  return (
    <>
      <div ref={box} className="flex gap-1 overflow-x-auto pb-1" onScroll={() => menu && setMenu(null)}>
        {times.map((t) => {
          const a = atOf(day, t);
          const inside = from != null && to != null && t >= from && t <= to;
          const badge = t === fromAt && from != null ? `начало ${clock(from)}` : t === toAt && to != null ? `конец ${clock(to)}` : null;
          return (
            <div key={`${camera}-${a}`} data-center={t === center ? "1" : undefined} className={`flex-none ${wide ? "w-[150px]" : "w-[124px]"}`}>
              <Tile
                camera={camera}
                at={a}
                w={320}
                lazy
                active={t === center}
                tint={inside || badge != null}
                badge={badge}
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setMenu({ t, x: r.left + r.width / 2, y: r.bottom + 4 });
                }}
              />
            </div>
          );
        })}
      </div>
      {menu && (
        <div
          data-strip-menu
          className="fixed z-[60] flex w-[190px] -translate-x-1/2 flex-col overflow-hidden rounded-[var(--radius-ui)] border border-border bg-bg py-1 text-[12.5px] shadow-lg"
          style={{ left: Math.max(100, Math.min(window.innerWidth - 100, menu.x)), top: Math.min(window.innerHeight - 110, menu.y) }}
        >
          <div className="px-3 pb-1 text-[11px] text-fg-muted tabular-nums">кадр {clock(menu.t)}</div>
          <button type="button" className="px-3 py-1.5 text-left hover:bg-bg-subtle" onClick={pick(onPick)}>
            Показать этот кадр
          </button>
          <button type="button" className="px-3 py-1.5 text-left hover:bg-bg-subtle" onClick={pick(onFrom)}>
            ⟦ Начало ролика
          </button>
          <button type="button" className="px-3 py-1.5 text-left hover:bg-bg-subtle" onClick={pick(onTo)}>
            Конец ролика ⟧
          </button>
        </div>
      )}
    </>
  );
}

type Job = {
  id: string;
  camera: Cam;
  from: string;
  to: string;
  status: "queued" | "running" | "ready" | "error";
  key?: string | undefined;
  size?: number | undefined;
  error?: string | undefined;
  startedAt?: number | undefined;
};
const jobId = (camera: number, from: string, to: string) => `${camera}-${from}-${to}`;

export function VideoStudio({
  locationId,
  cameras: fallback = [],
  eventCameras = [],
  selected: initialSelected = [],
  camera: initialCamera = null,
  at,
  from: initialFrom = null,
  to: initialTo = null,
  view: initialView = "one",
  events = [],
  chatId = null,
  dealId = null,
  caption = "",
  autoFocus = false,
  onChange,
}: {
  /** Салон: из него все камеры; нет — только `cameras`. */
  locationId: number | null;
  cameras?: { id: number; name: string }[];
  /** Камеры события: помечены точкой, кнопка «камеры события». */
  eventCameras?: number[];
  /** Начальный выбор; пусто — все камеры салона. */
  selected?: number[];
  camera?: number | null;
  at: string;
  from?: string | null;
  to?: string | null;
  view?: StudioView;
  /** События сделки — треугольники на шкале. */
  events?: DealVideoEvent[];
  /** Открыли из чата клиента — у роликов «Отправить клиенту». */
  chatId?: number | null;
  dealId?: number | null;
  caption?: string;
  autoFocus?: boolean;
  /** Экран «Видео» держит выбор в адресе. */
  onChange?: (v: StudioState) => void;
}) {
  const day = dayOf(at);
  const wide = useWide();
  const camsQ = useQuery(videoCamerasQuery(locationId));
  const all: Cam[] = useMemo(() => {
    const list: Cam[] = camsQ.data?.length ? camsQ.data : [];
    const extra = fallback.filter((c) => !list.some((l) => l.id === c.id));
    return [...list, ...extra];
  }, [camsQ.data, fallback]);
  const [sel, setSel] = useState<number[] | null>(initialSelected.length ? initialSelected : null);
  const chosen = useMemo(() => (sel ?? all.map((c) => c.id)).filter((id) => all.some((c) => c.id === id)), [sel, all]);
  const [activeId, setActiveId] = useState<number | null>(initialCamera);
  const active = chosen.includes(activeId ?? -1) ? (activeId as number) : (chosen[0] ?? null);
  const cam = (id: number) => all.find((c) => c.id === id) ?? { id, name: `Камера ${id}` };
  const [view, setView] = useState<StudioView>(initialView);
  const [expanded, setExpanded] = useState(false);

  const [cur, setCur] = useState(secOf(at));
  const [win, setWin] = useState<Win>(() => (initialView === "grid" && !initialFrom ? DAY_WIN : around(secOf(at), 1800)));
  const [step, setStep] = useState(10);
  const [from, setFrom] = useState<number | null>(initialFrom ? secOf(initialFrom) : null);
  const [to, setTo] = useState<number | null>(initialTo ? secOf(initialTo) : null);
  const curAt = atOf(day, cur);
  const tilesAt = useDebounced(curAt, 150);

  // Курсор ушёл за край окна клавишами — окно сдвигается так, чтобы курсор встал в треть от края.
  const goto = useCallback((s: number) => {
    const t = Math.max(0, Math.min(DAY - 1, Math.round(s)));
    setCur(t);
    setWin((w) => (t < w.start ? clampWin({ ...w, start: t - w.span / 3 }) : t > w.start + w.span ? clampWin({ ...w, start: t - (w.span * 2) / 3 }) : w));
  }, []);

  const changed = useRef(onChange);
  changed.current = onChange;
  useEffect(() => {
    if (active == null) return;
    changed.current?.({ camera: active, cams: sel ?? [], at: curAt, from: from != null ? atOf(day, from) : null, to: to != null ? atOf(day, to) : null, view });
  }, [active, sel, curAt, from, to, view, day]);

  // Предзагрузка соседних кадров активной камеры, когда курсор постоял.
  useEffect(() => {
    if (active == null) return;
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      for (const d of [-step, step]) loadFrame(frameUrl(active, atOf(day, cur + d), 1280), ctrl.signal).catch(() => {});
    }, 600);
    return () => (clearTimeout(t), ctrl.abort());
  }, [active, cur, step, day]);

  const marks: Mark[] = events.filter((e) => dayOf(e.at) === day).map((e) => ({ at: secOf(e.at), title: [e.title, e.detail, e.user].filter(Boolean).join(" · ") }));

  const toggle = (id: number, e?: React.MouseEvent) => {
    if (e?.altKey) return setActiveId(id);
    if (chosen.includes(id)) {
      if (chosen.length === 1) return;
      setSel(chosen.filter((x) => x !== id));
    } else setSel([...chosen, id]);
  };

  // Ролики: по задаче на камеру, по 2 разом; закрыли окно — опрос сервера прекращается.
  const [jobs, setJobs] = useState<Job[]>([]);
  const [justMade, setJustMade] = useState<string | null>(null);
  const [tab, setTab] = useState<"view" | "help">("view");
  const qc = useQueryClient();
  const historyQ = useQuery(videoClipsQuery(dealId != null ? { deal: dealId } : { location: locationId, day }));
  const abort = useRef(new AbortController());
  useEffect(() => {
    const ctrl = new AbortController();
    abort.current = ctrl;
    return () => ctrl.abort();
  }, []);
  const patch = (id: string, p: Partial<Job>) => setJobs((js) => js.map((j) => (j.id === id ? { ...j, ...p } : j)));
  useEffect(() => {
    const running = jobs.filter((j) => j.status === "running").length;
    const next = jobs.filter((j) => j.status === "queued").slice(0, Math.max(0, 2 - running));
    if (!next.length) return;
    const ids = new Set(next.map((n) => n.id));
    setJobs((js) => js.map((j) => (ids.has(j.id) ? { ...j, status: "running", startedAt: Date.now() } : j)));
    const signal = abort.current.signal;
    for (const n of next)
      makeClip(n.camera.id, n.from, n.to, signal, { deal_id: dealId, chat_id: chatId }).then(
        (clip) => (patch(n.id, { status: "ready", key: clip.key, size: clip.size }), setJustMade(clip.key), qc.invalidateQueries({ queryKey: ["video-clips"] })),
        (e: Error) => signal.aborted || patch(n.id, { status: "error", error: e.message }),
      );
    // Строка истории появляется с первым запросом — обновляем список, не дожидаясь готовности.
    setTimeout(() => qc.invalidateQueries({ queryKey: ["video-clips"] }), 1500);
  }, [jobs, dealId, chatId, qc]);

  const length = from != null && to != null ? to - from : null;
  const badLength = length != null && (length < 3 || length > 600);
  const fragQ = useQuery({ ...videoFragmentsQuery(active ?? 0, day), enabled: active != null });
  const covered =
    from != null && to != null && fragQ.data ? spans(fragQ.data.fragments, day).reduce((s, [a, b]) => s + Math.max(0, Math.min(b, to) - Math.max(a, from)), 0) : null;
  const order = active != null ? [active, ...chosen.filter((id) => id !== active)] : chosen;
  // Поставить в очередь отрезок камеры; уже собранный или собирающийся — не дублируем.
  const enqueue = (list: { camera: Cam; from: string; to: string }[]) =>
    setJobs((js) => {
      const out = [...js];
      const fresh: Job[] = [];
      for (const x of list) {
        const id = jobId(x.camera.id, x.from, x.to);
        const old = out.findIndex((j) => j.id === id);
        if (old >= 0) {
          const o = out[old]!;
          if (o.status === "error") out[old] = { ...o, status: "queued", error: undefined };
          continue;
        }
        const h = historyQ.data?.find((r) => jobId(r.camera_id, r.from, r.to) === id);
        if (h && (h.status === "ready" || h.status === "running")) continue;
        fresh.push({ id, camera: x.camera, from: x.from, to: x.to, status: "queued" });
      }
      return [...fresh, ...out];
    });
  const makeClips = () => {
    if (length == null || badLength) return;
    const f = atOf(day, from as number);
    const t = atOf(day, to as number);
    enqueue(order.map((id) => ({ camera: cam(id), from: f, to: t })));
  };
  // Список: задачи этого окна сверху, ниже — история (прошлые заходы и закрытые окна).
  const local = new Set(jobs.map((j) => j.id));
  const items: ClipItem[] = [
    ...jobs.map((j) => ({
      id: j.id, camera: j.camera.name, from: j.from, to: j.to, status: j.status, key: j.key, size: j.size,
      error: j.error, startedAt: j.startedAt, retry: () => patch(j.id, { status: "queued", error: undefined }),
    })),
    ...(historyQ.data ?? [])
      .filter((r) => !local.has(jobId(r.camera_id, r.from, r.to)))
      .map((r) => ({
        id: jobId(r.camera_id, r.from, r.to), camera: r.camera, from: r.from, to: r.to, status: r.status,
        key: r.status === "ready" ? r.key : undefined, size: r.size, error: r.error, user: r.user,
        retry: () => enqueue([{ camera: { id: r.camera_id, name: r.camera }, from: r.from, to: r.to }]),
      })),
  ];  // prettier-ignore

  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (autoFocus) root.current?.focus({ preventScroll: true });
  }, [autoFocus]);
  const onKey = (e: React.KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
    const k = e.key;
    if (k === "Enter" && (tag === "BUTTON" || tag === "A")) return;
    const handled = () => e.preventDefault();
    if (k === "ArrowLeft" || k === "ArrowRight") {
      const d = (k === "ArrowLeft" ? -1 : 1) * (e.ctrlKey || e.metaKey ? 1 : e.shiftKey ? step * 5 : step);
      goto(cur + d);
      return handled();
    }
    if (k === "[") return (setFrom(cur), handled());
    if (k === "]") return (setTo(cur), handled());
    if (k === "Home" && from != null) return (goto(from), handled());
    if (k === "End" && to != null) return (goto(to), handled());
    if (k === "+" || k === "=") return (setWin(around(cur, win.span / 1.6)), handled());
    if (k === "-") return (setWin(around(cur, win.span * 1.6)), handled());
    if (k === "0") return (setWin(DAY_WIN), handled());
    if (/^[1-9]$/.test(k) && chosen[Number(k) - 1] != null) return (setActiveId(chosen[Number(k) - 1] ?? null), handled());
    if (k === "g" || k === "G" || k === "п" || k === "П") return (setView((v) => (v === "one" ? "grid" : "one")), handled());
    if (k === "Enter") return (makeClips(), handled());
  };

  if (camsQ.isPending && locationId != null && !fallback.length) return <div className="text-[12.5px] text-fg-muted">Загружаю камеры салона…</div>;
  if (active == null) return <div className="text-[12.5px] text-fg-muted">{camsQ.isError ? `Камеры не загрузились: ${camsQ.error.message}` : "У салона нет камер Macroscop."}</div>;

  const others = chosen.filter((id) => id !== active);
  const groups = new Map<string, Cam[]>();
  for (const c of all) groups.set(c.grp || "Прочее", [...(groups.get(c.grp || "Прочее") ?? []), c]);
  const rank = (g: string) => {
    const i = GROUPS.findIndex((x) => g.startsWith(x));
    return i < 0 ? GROUPS.length : i;
  };
  const chipOf = (c: Cam) => {
    const on = chosen.includes(c.id);
    return (
      <button
        key={c.id}
        type="button"
        onClick={(e) => toggle(c.id, e)}
        title={[eventCameras.includes(c.id) && "камера события", c.status && c.status !== "Ok" && c.status, "клик — выбрать / убрать, Alt+клик — смотреть её"].filter(Boolean).join(" · ")}
        className={`${chip} ${on ? "border-accent bg-accent/10 text-accent-text" : "border-border hover:bg-bg-subtle"} ${c.id === active ? "ring-1 ring-accent" : ""}`}
      >
        {eventCameras.includes(c.id) && <span className="text-accent-text">●</span>}
        {c.name}
        {c.status && c.status !== "Ok" && <span className="size-1.5 rounded-full bg-err" />}
      </button>
    );
  };
  const gridCols = chosen.length <= 4 ? "grid-cols-2" : chosen.length <= 9 ? "grid-cols-3" : "grid-cols-4";

  const tabs = (
    <div className="flex gap-1 border-b border-border" data-annot="вкладки видео">
      {(
        [
          ["view", "Просмотр", Video],
          ["help", "Инструкция", BookOpen],
        ] as const
      ).map(([v, name, Icon]) => (
        <button
          key={v}
          type="button"
          onClick={() => setTab(v)}
          className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-[12.5px] ${tab === v ? "border-accent font-medium text-accent-text" : "border-transparent text-fg-muted hover:text-fg"}`}
        >
          <Icon size={14} /> {name}
        </button>
      ))}
    </div>
  );
  if (tab === "help")
    return (
      <div className="flex flex-col gap-3 text-[13px]" data-annot="инструкция видео">
        {tabs}
        <div className="max-w-[860px]">
          <Prose>{help}</Prose>
        </div>
      </div>
    );

  return (
    <div ref={root} tabIndex={-1} onKeyDown={onKey} className="flex flex-col gap-3 text-[12.5px] outline-none" data-annot="просмотр камер">
      {tabs}
      <div className="flex flex-col gap-1.5" data-annot="камеры">
        <div className="flex flex-wrap items-center gap-1">
          <span className="mr-1 text-fg-muted">Камеры:</span>
          {!expanded && chosen.slice(0, 8).map((id) => chipOf(cam(id)))}
          {!expanded && chosen.length > 8 && (
            <button type="button" className={`${chip} border-border text-fg-muted`} onClick={() => setExpanded(true)}>
              +{chosen.length - 8}
            </button>
          )}
          <button type="button" className={`${chip} border-dashed border-border text-fg-muted hover:bg-bg-subtle`} onClick={() => setExpanded(!expanded)}>
            {expanded ? "свернуть ▴" : `+ камеры салона (${all.length}) ▾`}
          </button>
          {eventCameras.length > 0 && (
            <button type="button" className="px-1 text-[12px] text-accent-text hover:underline" onClick={() => (setSel(eventCameras), setActiveId(eventCameras[0] ?? null))}>
              камеры события
            </button>
          )}
          <button type="button" className="px-1 text-[12px] text-accent-text hover:underline" onClick={() => setSel(all.map((c) => c.id))}>
            все
          </button>
          <div className="ml-auto flex overflow-hidden rounded-[var(--radius-ui)] border border-border" title="Вид (G)">
            {(
              [
                ["one", "Одна", Square],
                ["grid", "Сетка", LayoutGrid],
              ] as const
            ).map(([v, name, Icon]) => (
              <button key={v} type="button" onClick={() => setView(v)} className={`inline-flex h-7 items-center gap-1 px-2 text-[12px] ${view === v ? "bg-accent/15 text-accent-text" : "hover:bg-bg-subtle"}`}>
                <Icon size={13} /> {name}
              </button>
            ))}
          </div>
        </div>
        {expanded && (
          <div className="flex flex-col gap-1 rounded-[var(--radius-card)] border border-border p-2">
            {[...groups.entries()]
              .sort(([a], [b]) => rank(a) - rank(b))
              .map(([g, list]) => (
                <div key={g} className="flex flex-wrap items-center gap-1">
                  <span className="w-[110px] flex-none truncate text-[11.5px] text-fg-muted">{g}</span>
                  {list.map(chipOf)}
                </div>
              ))}
            {locationId == null && <div className="text-[11.5px] text-fg-muted">Салон события не определён — только камеры события.</div>}
          </div>
        )}
      </div>

      <Timeline camera={active} day={day} cur={cur} from={from} to={to} win={win} marks={marks} onWin={setWin} onPick={goto} onRange={(f, t) => (setFrom(f), setTo(t))} />

      {view === "grid" ? (
        <div className={`grid gap-2 ${gridCols}`} data-annot="сетка камер">
          {chosen.map((id) => (
            <Tile
              key={`${id}-${tilesAt}`}
              camera={id}
              at={tilesAt}
              w={chosen.length > 9 ? 480 : 640}
              title={cam(id).name}
              active={id === active}
              onClick={() => setActiveId(id)}
              onDoubleClick={() => (setActiveId(id), setView("one"))}
            />
          ))}
        </div>
      ) : (
        <div className={wide && others.length ? "grid grid-cols-[minmax(0,1fr)_260px] gap-2" : "flex flex-col gap-2"}>
          <div className="mx-auto w-full max-w-[calc(52vh*16/9)]">
            <BigFrame camera={active} at={curAt} name={cam(active).name} />
          </div>
          {others.length > 0 && (
            <div className={wide ? `grid max-h-[52vh] content-start gap-1.5 overflow-y-auto ${others.length >= 7 ? "grid-cols-2" : ""}` : "flex gap-1.5 overflow-x-auto"} data-annot="синхронные кадры">
              {others.map((id) => (
                <div key={id} className={wide ? "" : "w-[160px] flex-none"}>
                  <Tile key={`${id}-${tilesAt}`} camera={id} at={tilesAt} w={480} title={cam(id).name} onClick={() => setActiveId(id)} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-1.5" data-annot="кадры вокруг">
        <div className="flex flex-wrap items-center gap-1.5">
          <button type="button" className={btn} onClick={() => goto(cur - step)} title="Назад на шаг (←, Shift — 5 шагов, Ctrl — 1 с)">
            <ChevronLeft size={15} />
          </button>
          <input
            type="time"
            step={1}
            value={clock(cur)}
            onChange={(e) => e.target.value && goto(secOf(`${day}T${e.target.value.padEnd(8, ":00")}`))}
            className="h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 tabular-nums"
          />
          <button type="button" className={btn} onClick={() => goto(cur + step)} title="Вперёд на шаг (→)">
            <ChevronRight size={15} />
          </button>
          <span className="ml-1 text-fg-muted">шаг</span>
          {STEPS.map(([s, name]) => (
            <button key={s} type="button" onClick={() => setStep(s)} className={`rounded-full px-2 py-0.5 text-[12px] ${s === step ? "bg-accent/15 text-accent-text" : "text-fg-muted hover:bg-bg-subtle"}`}>
              {name}
            </button>
          ))}
          <span className="ml-auto flex gap-1.5">
            <button type="button" className={btn} onClick={() => setFrom(cur)} title="Начало ролика = курсор ([)">
              ⟦ Начало = {clock(cur)}
            </button>
            <button type="button" className={btn} onClick={() => setTo(cur)} title="Конец ролика = курсор (])">
              Конец = {clock(cur)} ⟧
            </button>
          </span>
        </div>
        <FrameStrip camera={active} day={day} center={secOf(tilesAt)} step={step} from={from} to={to} wide={wide} onPick={goto} onFrom={setFrom} onTo={setTo} />
      </div>

      <div className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-2" data-annot="отрезок ролика">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">Ролик</span>
          {from != null || to != null ? (
            <span className="inline-flex items-center gap-1 tabular-nums">
              <button type="button" className="hover:underline" disabled={from == null} onClick={() => from != null && goto(from)} title="К началу (Home)">
                {from != null ? clock(from) : "—"}
              </button>
              →
              <button type="button" className="hover:underline" disabled={to == null} onClick={() => to != null && goto(to)} title="К концу (End)">
                {to != null ? clock(to) : "—"}
              </button>
              {length != null && length > 0 && (
                <span className="text-fg-muted">
                  · {dur(length)} · {order.length} {order.length === 1 ? "камера" : order.length < 5 ? "камеры" : "камер"} · ≈ {dur(Math.max(5, Math.ceil(order.length / 2) * Math.ceil(length / 6)))}
                </span>
              )}
              <button type="button" onClick={() => (setFrom(null), setTo(null))} title="Сбросить отрезок" className="grid size-6 place-items-center rounded-full text-fg-muted hover:bg-bg">
                <X size={13} />
              </button>
            </span>
          ) : (
            <span className="text-fg-muted">отметьте «Начало» и «Конец» кнопками, клавишами [ и ] или ручками на шкале</span>
          )}
          <span className="ml-auto flex min-w-0 flex-wrap items-center gap-2">
            <button type="button" className={primary} disabled={length == null || badLength} onClick={makeClips} title="Enter">
              <Film size={14} /> {order.length === 1 ? "Сделать ролик" : `${order.length} ${order.length < 5 ? "ролика" : "роликов"}`}
            </button>
          </span>
        </div>
        {badLength && <div className="text-[11.5px] text-err">Ролик — от 3 секунд до 10 минут, конец позже начала.</div>}
        {!badLength && length != null && covered != null && covered < length / 2 && (
          <div className="text-[11.5px] text-fg-muted">У «{cam(active).name}» в отрезке почти нет записи — ролик может быть коротким или пустым.</div>
        )}
        <ClipList
          items={items}
          chatId={chatId}
          dealId={dealId}
          caption={caption}
          show={justMade}
          empty={historyQ.isPending ? null : dealId != null ? "Роликов по сделке ещё нет." : "Роликов этого салона за день ещё нет."}
        />
      </div>
      <div className="text-[11px] text-fg-muted">
        Клавиши: ← → шаг · Shift — 5 шагов · Ctrl — 1 с · [ ] — начало/конец · + − 0 — масштаб шкалы · 1–9 — камера · G — сетка · Enter — ролик
      </div>
    </div>
  );
}
